import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import { getUserAuthInfo, waitForUserAuth, renewToken, forceLogout, isHardAuthFailure } from "./utils/AuthService";
import { getUserInfo } from "./api/General/account";
import { connect } from "react-redux";
import { init, setLoading } from "./store/redux/actions/userActionCreator";
import LandingPage from "./components/LandingPage";

const PrivateRoute = (props: any) => {
    const { t } = useTranslation();
    const isInitializing = useRef(false);

    useEffect(() => {
        const fetchUser = async () => {
            if (isInitializing.current) return;

            try {
                if (props.firstLoad) {
                    isInitializing.current = true;

                    if (!props.userAuthInfo) {
                        let userAuthInfo = await getUserAuthInfo();
                        if (!userAuthInfo) {
                            userAuthInfo = await waitForUserAuth(1500, 50);
                        }

                        if (userAuthInfo) {
                            if (userAuthInfo.expired) {
                                try {
                                    // renewToken fires addUserLoaded on
                                    // success, which authLifecycle turns into
                                    // a SET_USER_AUTH_INFO dispatch.
                                    const refreshed = await renewToken();
                                    if (!refreshed) {
                                        await forceLogout('boot-renew-returned-null');
                                        return;
                                    }
                                    userAuthInfo = refreshed;
                                } catch (e) {
                                    // Only tear down the session on a hard
                                    // "your IdP session is gone" error. A
                                    // transient network/timeout at boot
                                    // shouldn't kick the user out — the 401
                                    // interceptor will retry renewal on the
                                    // next API call once the network recovers.
                                    if (isHardAuthFailure(e)) {
                                        await forceLogout('boot-renew-failed-hard');
                                        return;
                                    }
                                    console.warn('[auth] boot-time renew failed (transient, continuing with stale token):', e);
                                    // fall through with the stale userAuthInfo — getUserInfo
                                    // below may still 401, which the interceptor handles.
                                }
                            }
                            const userInfo = await getUserInfo();
                            props.init(userInfo.payload as any);
                        } else {
                            props.setLoading(false);
                        }
                    } else {
                        const userInfo = await getUserInfo();
                        props.init(userInfo.payload as any);
                    }
                }
            } catch (error) {
                console.error("Error fetching user:", error);
                props.setLoading(false);
            } finally {
                isInitializing.current = false;
            }
        };
        fetchUser();
    }, [props.firstLoad]);

    if (props.loading) {
        return <p>{t('Loading...')}</p>;
    }

    if (!props.isLoggedIn) {
        return <LandingPage />;
    }

    return <Outlet />;
};


const mapStateToProps = (state: any) => {
    return {
        loading: state.user.loading,
        firstLoad: state.user.firstLoad,
        isLoggedIn: state.user.isLoggedIn,
        userAuthInfo: state.user.userAuthInfo,
        userId: state.user.userId,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        init: (input: any) => dispatch(init(input)),
        setLoading: (input: any) => dispatch(setLoading(input)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);
